import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import {
  Button,
  Card,
  Cell,
  Column,
  Row,
  Select,
  type SelectOption,
  Table,
  TableBody,
  TableContainer,
  TableHeader,
  TextField,
} from '@skupreme/shared/ui';
import {
  type DraftErrors,
  type InternationalRow,
  MARKETPLACE_SPEEDS,
  type SpeedRow,
} from '../../lib/shipping-template';
import { CellError } from './MethodCard';

const SPEED_OPTIONS = MARKETPLACE_SPEEDS.map(speed => ({
  value: speed,
  label: speed,
}));

/* A row may keep its own pick; the others' picks are out of its list. */
function optionsFor(options: SelectOption[], used: string[], own: string) {
  return options.filter(
    option => option.value === own || !used.includes(option.value),
  );
}

function DeleteButton({
  label,
  isDisabled,
  onPress,
}: {
  label: string;
  isDisabled: boolean;
  onPress: () => void;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      shape="circle"
      aria-label={label}
      isDisabled={isDisabled}
      onPress={onPress}
    >
      <TrashIcon size={14} aria-hidden />
    </Button>
  );
}

type SpeedsCardProps = {
  speeds: SpeedRow[];
  marketplaceOptions: SelectOption[];
  errors: DraftErrors['speeds'];
  isDisabled: boolean;
  onAdd: () => void;
  onChange: (id: string, patch: Partial<SpeedRow>) => void;
  onDelete: (id: string) => void;
};

/** The speed a marketplace's orders get when they name none. */
export function SpeedsCard({
  speeds,
  marketplaceOptions,
  errors,
  isDisabled,
  onAdd,
  onChange,
  onDelete,
}: SpeedsCardProps) {
  const used = speeds.map(speed => speed.marketplaceId);

  return (
    <Card
      title="Default marketplace speeds"
      action={
        <Button
          size="sm"
          variant="pill"
          isDisabled={isDisabled || speeds.length >= marketplaceOptions.length}
          onPress={onAdd}
        >
          <PlusIcon size={14} aria-hidden />
          Add speed
        </Button>
      }
      bodyClassName="sk:p-0"
    >
      <TableContainer>
        <Table aria-label="Default marketplace speeds">
          <TableHeader>
            <Column id="marketplace" isRowHeader>
              Marketplace
            </Column>
            <Column id="speed">Shipping speed</Column>
            <Column id="actions" aria-label="Actions" className="sk:w-12">
              {''}
            </Column>
          </TableHeader>
          <TableBody
            renderEmptyState={() => (
              <div className="sk:px-3 sk:py-8 sk:text-center sk:text-fg-tertiary">
                No default speeds
              </div>
            )}
          >
            {speeds.map(speed => (
              <Row key={speed.id} id={speed.id}>
                <Cell>
                  <div className="sk:flex sk:flex-col sk:gap-0.5">
                    <Select
                      aria-label="Marketplace"
                      options={optionsFor(
                        marketplaceOptions,
                        used,
                        speed.marketplaceId,
                      )}
                      value={speed.marketplaceId}
                      onChange={marketplaceId =>
                        onChange(speed.id, { marketplaceId })
                      }
                      placeholder="Select marketplace"
                      isDisabled={isDisabled}
                      isInvalid={!!errors[speed.id]?.marketplaceId}
                      className="sk:w-56"
                    />
                    <CellError>{errors[speed.id]?.marketplaceId}</CellError>
                  </div>
                </Cell>
                <Cell>
                  <div className="sk:flex sk:flex-col sk:gap-0.5">
                    <Select
                      aria-label="Shipping speed"
                      options={SPEED_OPTIONS}
                      value={speed.speed}
                      onChange={value => onChange(speed.id, { speed: value })}
                      placeholder="Select speed"
                      isDisabled={isDisabled}
                      isInvalid={!!errors[speed.id]?.speed}
                      className="sk:w-40"
                    />
                    <CellError>{errors[speed.id]?.speed}</CellError>
                  </div>
                </Cell>
                <Cell>
                  <DeleteButton
                    label="Delete speed"
                    isDisabled={isDisabled}
                    onPress={() => onDelete(speed.id)}
                  />
                </Cell>
              </Row>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

type InternationalCardProps = {
  rows: InternationalRow[];
  regionOptions: SelectOption[];
  currency: string;
  isDisabled: boolean;
  onAdd: () => void;
  onChange: (id: string, patch: Partial<InternationalRow>) => void;
  onDelete: (id: string) => void;
};

/** Flat fees for orders shipped out of the provider's own region. */
export function InternationalCard({
  rows,
  regionOptions,
  currency,
  isDisabled,
  onAdd,
  onChange,
  onDelete,
}: InternationalCardProps) {
  const used = rows.map(row => row.regionId);

  return (
    <Card
      title="International shipping"
      action={
        <Button
          size="sm"
          variant="pill"
          isDisabled={isDisabled || rows.length >= regionOptions.length}
          onPress={onAdd}
        >
          <PlusIcon size={14} aria-hidden />
          Add region
        </Button>
      }
      bodyClassName="sk:p-0"
    >
      <TableContainer>
        <Table aria-label="International shipping">
          <TableHeader>
            <Column id="region" isRowHeader>
              Region
            </Column>
            <Column id="price">Shipping fee ({currency})</Column>
            <Column id="actions" aria-label="Actions" className="sk:w-12">
              {''}
            </Column>
          </TableHeader>
          <TableBody
            renderEmptyState={() => (
              <div className="sk:px-3 sk:py-8 sk:text-center sk:text-fg-tertiary">
                No international shipping
              </div>
            )}
          >
            {rows.map(row => (
              <Row key={row.id} id={row.id}>
                <Cell>
                  <Select
                    aria-label="Region"
                    options={optionsFor(regionOptions, used, row.regionId)}
                    value={row.regionId}
                    onChange={regionId => onChange(row.id, { regionId })}
                    isDisabled={isDisabled}
                    className="sk:w-56"
                  />
                </Cell>
                <Cell>
                  <TextField
                    label="Shipping fee"
                    type="number"
                    placeholder="1.00"
                    value={row.price}
                    onChange={price => onChange(row.id, { price })}
                    isDisabled={isDisabled}
                    className="sk:w-20"
                  />
                </Cell>
                <Cell>
                  <DeleteButton
                    label="Delete region"
                    isDisabled={isDisabled}
                    onPress={() => onDelete(row.id)}
                  />
                </Cell>
              </Row>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}
